import { CellContext, RowData } from "@tanstack/react-table";
import {
  Collaborator,
  EscalationReason,
  OriginAuthor,
  ResolutionStatus,
  ResolvedReason,
  SkillUsed,
  SkipReason,
  Team,
} from "@/v2/entities/explore/Explore.model";
import { EscalationReasonFormatter } from "../formatters/EscalationReasonFormatter";
import { CollaboratorsFormatter } from "@/v2/entities/explore/formatters/CollaboratorsFormatter";
import { TeamFormatter } from "@/v2/entities/explore/formatters/TeamFormatter";
import { TimeToResolutionFormatter } from "../formatters/TimeToResolutionFormatter";
import { TimeToFirstResponseFormatter } from "../formatters/TimeToFirstResponseFormatter";
import { Badge, Flex, IconButton, Text } from "@radix-ui/themes";
import { ResolutionStatusFormatter } from "../formatters/ResolutionStatusFormatter";
import { SkipReasonFormatter } from "../formatters/SkipReasonFormatter";
import { ResolvedReasonFormatter } from "../formatters/ResolvedReasonFormatter";
import { OriginAuthorFormatter } from "../formatters/OriginAuthorFormatter";
import { QuackAnsweredFormatter } from "../formatters/QuackAnsweredFormatter";
import { IsAnsweredByAuiFormatter } from "../formatters/IsAnsweredByAuiFormatter";
import { AvgSentimentFormatter } from "../formatters/AvgSentimentFormatter";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { TicketIdFormatter } from "../formatters/TicketIdFormatter";
import { SkillsUsedFormatter } from "../formatters/SkillsUsedFormatter";

export function displayIdCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <>{`#${value}`}</>;
}

export function resolutionStatusCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<ResolutionStatus | undefined>();

  if (value == null) {
    return <></>;
  }

  return <ResolutionStatusFormatter value={value} />;
}

export function groupByResolutionStatusCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<ResolutionStatus>();

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      <ResolutionStatusFormatter value={value} />
    </Text>
  );
}

export function escalationReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<EscalationReason | undefined>();

  if (value == null) {
    return <></>;
  }

  return <EscalationReasonFormatter value={value} />;
}

export function groupByEscalationReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<EscalationReason>();

  return (
    <Text size={"2"} weight={"medium"} highContrast>
      <EscalationReasonFormatter value={value} />
    </Text>
  );
}

export function skipReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<SkipReason | undefined>();

  if (value == null) {
    return <></>;
  }

  return <SkipReasonFormatter value={value} />;
}

export function originAuthorCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<OriginAuthor | undefined>();
  if (value == null) {
    return <></>;
  }

  return <OriginAuthorFormatter value={value} />;
}

export function scoreTitleCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string>();

  return <>{value}</>;
}

export function avgQaScoreByTitleCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number>();

  return <AvgQaScoreFormatter value={value} />;
}

export function groupBySkipReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<SkipReason>();

  return <SkipReasonFormatter value={value} />;
}

export function resolvedReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<ResolvedReason | undefined>();

  if (value == null) {
    return <></>;
  }

  return <ResolvedReasonFormatter value={value} />;
}

export function groupByResolvedReasonCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<ResolvedReason>();

  return <ResolvedReasonFormatter value={value} />;
}

export function groupByScoreTitleCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string>();

  return <>{value}</>;
}

export function collaboratorsCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<Collaborator[] | undefined>();

  return <CollaboratorsFormatter value={value} />;
}

export function validationStatusCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean | undefined>();

  return (
    <Badge variant={value ? "solid" : "soft"} color={value ? "green" : "gray"}>
      {value ? "Validated" : "Pending"}
    </Badge>
  );
}

export function teamsCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<Team[] | undefined>();

  return <TeamFormatter value={value} />;
}

export function ticketIdCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<string | undefined>();

  if (value == null) {
    return <></>;
  }

  return <TicketIdFormatter value={value} />;
}

export function timeToFirstResponseCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <TimeToFirstResponseFormatter value={value} />;
}

export function timeToResolutionCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <TimeToResolutionFormatter value={value} />;
}

export function isQuackAnsweredCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean | undefined>();

  if (value == null) {
    return <></>;
  }

  return <QuackAnsweredFormatter value={value} />;
}

export function groupByIsQuackAnsweredCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean>();

  return <QuackAnsweredFormatter value={value} size="2" />;
}

export function isAnsweredByAuiCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean | undefined>();

  if (value == null) {
    return <></>;
  }

  return <IsAnsweredByAuiFormatter value={value} />;
}

export function groupByIsAnsweredByAuiCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<boolean>();

  return <IsAnsweredByAuiFormatter value={value} size="2" />;
}

export function avgSentimentCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<number | undefined>();

  if (value == null) {
    return <></>;
  }

  return <AvgSentimentFormatter value={value} />;
}

export function skillsUsedCell<RowDataType extends RowData>({
  getValue,
}: CellContext<RowDataType, unknown>) {
  const value = getValue<SkillUsed[]>();

  return <SkillsUsedFormatter value={value} />;
}
